import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useTranslation } from "lib/i18n/use-translation";
import { Button } from "components/ui";
import { OriAvatar } from "components/ori";

import styles from "./stat-cards.module.scss";
import { BaseScore } from "apps/user/learner/api/types";
import { useInViews } from "utility/useInViews";
import { trackEvent } from "utility/mixpanel";
import {
  setOriStarterMessage,
  wasStarterSentRecently,
} from "apps/user/learner/lib/ori-starter-message";
import type { TapInEntryPoint } from "apps/user/learner/lib/tap-in";
import { indexTapIn, useTapIn } from "apps/user/learner/lib/use-tap-in";

const STARTER_PROMPT =
  "Can you walk me through my index score and what I should focus on?";

const ENTRY_POINT_BY_SOURCE: Record<string, string> = {
  "Ori Coach Card (Home)": "home_score_hook",
  "Ori Coach Card (Track)": "index_page_hook",
};

const TAP_IN_ENTRY_POINT_BY_SOURCE: Record<string, TapInEntryPoint> = {
  "Ori Coach Card (Home)": "web_home_index",
  "Ori Coach Card (Track)": "web_track_index",
};

interface OriCoachCardProps {
  overallIndexScore?: BaseScore;
  summary?: string;
  takenAt?: string;
  flush?: boolean;
  source: string;
}

const OriCoachCard: React.FC<OriCoachCardProps> = ({
  overallIndexScore,
  summary,
  takenAt,
  flush = false,
  source,
}) => {
  const scoreValue = overallIndexScore?.value;
  // Analytics only — never the display label, which is localised.
  const scoreBand = overallIndexScore?.labelKey;
  const { ref, inView } = useInViews();
  const navigate = useNavigate();
  const { t } = useTranslation(["components", "ori"]);
  const { enabled: tapInEnabled, messageFor, open: openTapIn } = useTapIn();

  const logImpression = useCallback(() => {
    trackEvent("Ori Coach Card Impression", {
      Source: source,
      "Index Score": scoreValue ?? null,
      "Score Band": scoreBand ?? null,
      "Has Score": Boolean(scoreValue),
    });
  }, [scoreBand, scoreValue, source]);

  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  if (!takenAt) {
    return null;
  }

  // A score without a value has nothing for Ori to answer from, and the Track
  // page's view event counts the entry point as shown only with a value.
  const tapInEntryPoint = TAP_IN_ENTRY_POINT_BY_SOURCE[source];
  const tapIn =
    tapInEnabled && tapInEntryPoint && overallIndexScore?.value != null
      ? indexTapIn(tapInEntryPoint, overallIndexScore)
      : null;

  const handleDiscussClick = () => {
    const reprompted = !wasStarterSentRecently(
      tapIn ? messageFor(tapIn) : STARTER_PROMPT
    );
    trackEvent("Ori Coach Card Clicked", {
      Source: source,
      // Analytics label stays English so the property doesn't fragment by locale.
      "Button Label": "Discuss your score with Ori",
      "Index Score": scoreValue ?? null,
      "Score Band": scoreBand ?? null,
      "Has Score": Boolean(scoreValue),
      Destination: "/user/chat",
      "Current Page": window.location.href,
      Reprompted: reprompted,
    });
    if (tapIn) {
      openTapIn(tapIn);
      return;
    }
    if (reprompted)
      setOriStarterMessage(
        STARTER_PROMPT,
        ENTRY_POINT_BY_SOURCE[source] ?? "direct"
      );
    navigate("/user/chat");
  };

  return (
    <div
      className={clsx(
        styles["ori-coach-card"],
        flush && styles["ori-coach-card--flush"]
      )}
      ref={ref}
      data-testid="ori-coach-card"
    >
      <div className={styles["ori-coach-identity"]}>
        <OriAvatar size={48} />
        <span className={styles["ori-coach-label"]}>{t("ori:brand.name")}</span>
      </div>
      <p className={styles["ori-coach-summary"]}>{summary}</p>

      <Button
        variant="enclosed-primary"
        monochromeColour="white"
        fullWidth
        className={styles["ori-coach-button"]}
        onClick={handleDiscussClick}
        data-testid="ori-coach-cta"
      >
        {t("components:oriCoachCard.discussCta")}
      </Button>
    </div>
  );
};

export default OriCoachCard;
